import type { DispatchPanel } from "@/components/Dispatch/DispatchPopover";
import { type DispatchTableRow, rowFeedId } from "./dispatchTableRow";
import { VIEW_QUERY_PARAM } from "./resolveActiveView";

/** The `?panel=` query parameter selecting the popover tab. */
export const PANEL_QUERY_PARAM = "panel";

/** The query string a Dispatch URL carries: the open tab when there is one, and `?view=` when the page has it. */
function dispatchQuery(viewId: string | null, panel?: DispatchPanel): string {
  const params = new URLSearchParams();
  if (panel) params.set(PANEL_QUERY_PARAM, panel);
  if (viewId) params.set(VIEW_QUERY_PARAM, viewId);
  const query = params.toString();
  return query ? `?${query}` : "";
}

/** The page's own URL, with `?view=` carried along. */
export function dispatchPagePath(workspaceId: string, viewId: string | null): string {
  return `/workspaces/${workspaceId}/dispatch${dispatchQuery(viewId)}`;
}

/** The URL of one driver's popover on one tab: `/dispatch/:membershipId/channel/:feedId?panel=`, `?view=` kept. */
export function dispatchPanelPath(
  workspaceId: string,
  membershipId: string,
  feedId: string,
  panel: DispatchPanel,
  viewId: string | null,
): string {
  return `/workspaces/${workspaceId}/dispatch/${membershipId}/channel/${feedId}${dispatchQuery(viewId, panel)}`;
}

/**
 * The URL an open popover moves to when the selection lands on `row`, or `null` when it stays where it is: the
 * popover is closed, the row belongs to the driver already open, or the row has no channel to show.
 *
 * @param open - The driver whose popover is open, or `null` while none is.
 */
export function followedPanelPath(
  workspaceId: string,
  open: { membershipId: string; panel: DispatchPanel } | null,
  row: DispatchTableRow,
  viewId: string | null,
): string | null {
  if (!open || row.driver.workspaceMembershipId === open.membershipId) return null;
  const feedId = rowFeedId(row);
  return feedId ? dispatchPanelPath(workspaceId, row.driver.workspaceMembershipId, feedId, open.panel, viewId) : null;
}

/** The tab a `?panel=` value names; History unless it says Context. */
export function panelFromParam(value: string | null): DispatchPanel {
  return value === "context" ? "context" : "history";
}
